import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2, Send, ArrowLeft, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

interface SuccessSearchParams {
  message?: string;
  refNumber?: string;
}

export const Route = createFileRoute("/forms/$slug/respond/success")({
  component: FormSuccessPage,
  validateSearch: (search: Record<string, unknown>): SuccessSearchParams => ({
    message: search.message as string | undefined,
    refNumber: search.refNumber as string | undefined,
  }),
});

function hasStoredSession(): boolean {
  try {
    const stored = localStorage.getItem("lara-portal-session");
    if (!stored) return false;
    const parsed = JSON.parse(stored);
    return Boolean(parsed?.token);
  } catch {
    return false;
  }
}

function FormSuccessPage() {
  const { slug } = Route.useParams();
  const { message, refNumber } = Route.useSearch();
  const isAuthenticated = hasStoredSession();

  const confirmationMessage =
    message || "Your response has been recorded successfully.";

  return (
    <div className="min-h-screen bg-gradient-to-b from-muted/30 to-background">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Success Card */}
        <div className="bg-card rounded-lg border shadow-sm overflow-hidden">
          <div className="h-2 bg-green-500" />
          <div className="p-8 sm:p-12 text-center">
            {/* Animated checkmark */}
            <div className="relative mx-auto w-20 h-20 mb-6 success-checkmark">
              <div className="absolute inset-0 bg-green-100 dark:bg-green-900/30 rounded-full" />
              <div className="absolute inset-0 flex items-center justify-center">
                <CheckCircle2 className="h-12 w-12 text-green-600 dark:text-green-400" />
              </div>
            </div>

            <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
              Response Submitted
            </h1>

            {/* Auto-number reference */}
            {refNumber && (
              <div className="mb-4 space-y-2">
                <div className="inline-flex items-center gap-2 bg-muted/50 border rounded-lg px-4 py-2">
                  <span className="text-sm text-muted-foreground">
                    Reference:
                  </span>
                  <span className="font-mono font-semibold text-foreground text-lg">
                    {refNumber}
                  </span>
                </div>
                <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">
                  Please take note of your reference number for future enquiries
                </p>
              </div>
            )}

            <p className="text-muted-foreground mb-8 max-w-md mx-auto">
              {confirmationMessage}
            </p>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/forms/$slug/respond"
                params={{ slug }}
              >
                <Button variant="outline" className="gap-2 w-full sm:w-auto">
                  <Send className="h-4 w-4" />
                  Submit Another Response
                </Button>
              </Link>
              {isAuthenticated ? (
                <Link to="/portal/myapp">
                  <Button variant="ghost" className="gap-2 w-full sm:w-auto">
                    <ArrowLeft className="h-4 w-4" />
                    Back to Tables
                  </Button>
                </Link>
              ) : (
                <a href="https://larasoft.global" rel="noopener noreferrer">
                  <Button variant="ghost" className="gap-2 w-full sm:w-auto">
                    <ExternalLink className="h-4 w-4" />
                    Go to Larasoft
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Additional info card */}
        <div className="mt-6 bg-card rounded-lg border shadow-sm p-6">
          <h2 className="font-medium mb-2">What happens next?</h2>
          <ul className="text-sm text-muted-foreground space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1">&bull;</span>
              Your response has been saved
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1">&bull;</span>
              You can submit additional responses using the button above
            </li>
          </ul>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t text-center">
          <p className="text-sm text-muted-foreground">
            Powered by <span className="font-medium">Lara MyApp</span>
          </p>
        </div>
      </div>
    </div>
  );
}
